import { DestroyRef, Directive, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, merge, tap } from 'rxjs';
import { FilterParams } from 'tabulator-tables';
import { IIKOTableGridComponent } from '../iiko-table-grid/iiko-table-grid.component';
import { TableGridRowDataType } from '../model';
import { DEFAULT_CHILDREN_FIELD_NAME } from '../model/constants';

@Directive({
  selector: '[restoTableGridTreeTableSearchString]',
})
export class TreeTableSearchStringDirective implements OnInit {
  /** A search string to filter rows in the table */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('restoTableGridTreeTableSearchString') set searchString(value: string) {
    this._onSetSearchTable$$.next(value);
  }

  destroyRef = inject(DestroyRef);

  private _onSetSearchTable$$ = new BehaviorSubject<string | undefined>(undefined);

  private _searchString: string | undefined = undefined;

  private _childToParentMap = new Map<TableGridRowDataType, TableGridRowDataType>();

  constructor(private tableGridComponent: IIKOTableGridComponent) {}

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    merge(this.tableGridComponent.dataLoaded$, this.tableGridComponent.dataChanged$)
      .pipe(
        tap((x: TableGridRowDataType[]) => this.recreateTreeIndex(x)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this._onSetSearchTable$$
      .pipe(
        tap((searchString: string | undefined) => {
          if (!searchString) {
            this.tableGridComponent.clearFilter(false);
            return;
          }

          this._searchString = searchString.toLowerCase();

          const filterCallback: (data: any, filterParams: any) => boolean =
            this.searchStringCallback.bind(this);

          this.tableGridComponent.setFilter(filterCallback);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private searchStringCallback(data: TableGridRowDataType, filterParams: FilterParams): boolean {
    const childField = this.tableGridComponent.dataTreeChildField ?? DEFAULT_CHILDREN_FIELD_NAME;

    if (this.containsSearchString(data, childField)) {
      return true;
    }

    if (this.containsSearchStringInParentNode(data, childField)) {
      return true;
    }

    if (!Object.prototype.hasOwnProperty.call(data, childField)) {
      return false;
    }

    const children = data[childField] as TableGridRowDataType[];

    for (const child of children) {
      if (this.searchStringCallback(child, filterParams)) {
        return true;
      }
    }

    return false;
  }

  private containsSearchString(data: TableGridRowDataType, childField: string): boolean {
    for (const entry of Object.entries(data)) {
      const [k, v] = entry;
      if (k === childField) {
        continue;
      }

      if ((v as any)?.toString().toLowerCase().includes(this._searchString)) {
        return true;
      }
    }

    return false;
  }

  private recreateTreeIndex(nodes: TableGridRowDataType[]): void {
    const childField = this.tableGridComponent.dataTreeChildField ?? DEFAULT_CHILDREN_FIELD_NAME;
    this._childToParentMap.clear();

    for (const node of nodes) {
      this.addChildToParentMap(node, childField);
    }
  }

  private addChildToParentMap(node: TableGridRowDataType, childField: string): void {
    const stack: TableGridRowDataType[] = [node];
    while (stack.length) {
      const currentNode = stack.pop() as TableGridRowDataType;
      if (!Object.prototype.hasOwnProperty.call(currentNode, childField)) {
        continue;
      }

      const childNodes = currentNode[childField] as TableGridRowDataType[];
      for (const childNode of childNodes) {
        this._childToParentMap.set(childNode, currentNode);
        stack.push(childNode);
      }
    }
  }

  private containsSearchStringInParentNode(
    data: TableGridRowDataType,
    childField: string
  ): boolean {
    const parentNode = this._childToParentMap.get(data);

    if (!parentNode) {
      return false;
    }

    if (this.containsSearchString(parentNode, childField)) {
      return true;
    }

    return this.containsSearchStringInParentNode(parentNode, childField);
  }
}
